import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { createFileRoute } from "@tanstack/react-router";
import { AdminPage } from "@/components/admin/AdminSidebar";
import { Button } from "@/components/ui/button";
import { Mail, Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { findAgenciasByEmailSubject, recountContadores } from "@/lib/tools.functions";
import { getScrapingbeeStats } from "@/lib/scrapingbee-stats.functions";
import {
  resolveProvinciaAgencia,
  assignProvinciaAgencia,
  resolveProvinciaBatch,
} from "@/lib/agencia-provincia.functions";
import {
  enrichAgenciaPreview,
  enrichAgenciasBatch,
} from "@/lib/agencia-enrich.functions";
import { AgenciaEnrichPreviewView } from "@/components/admin/AgenciaEnrichDialog";


import { AsyncEntityCombobox } from "@/components/admin/AsyncEntityCombobox";
import { useAgenciaFetcher } from "@/hooks/useEntityFetchers";
import { Switch } from "@/components/ui/switch";

export const Route = createFileRoute("/admin/tools/")({
  head: () => ({ meta: [{ title: "Tools — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: ToolsIndex,
});

function ToolsIndex() {
  const [open, setOpen] = useState(false);
  return (
    <AdminPage title="Tools" description="Herramientas internas para admins de Bizaclia.">
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => setOpen(true)}>
          <Mail className="mr-2 h-4 w-4" />
          Agencias con email enviados
        </Button>
        <RecalcularContadoresButton />

      </div>
      <AgenciasEmailDialog open={open} onOpenChange={setOpen} />
      <ProvinciaPorRegion />
      <EnriquecerAgencia />
      <ScrapingbeeUsage />
    </AdminPage>

  );
}

function RecalcularContadoresButton() {
  const [loading, setLoading] = useState(false);
  const run = useServerFn(recountContadores);
  return (
    <Button
      variant="outline"
      disabled={loading}
      onClick={async () => {
        setLoading(true);
        try {
          await run();
          toast.success("Contadores recalculados");
        } catch (e) {
          toast.error(e instanceof Error ? e.message : "Error");
        } finally {
          setLoading(false);
        }
      }}
    >
      {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
      Recalcular contadores
    </Button>
  );
}

function ProvinciaPorRegion() {
  const [agenciaId, setAgenciaId] = useState("");
  const [loading, setLoading] = useState(false);
  const [single, setSingle] = useState<Awaited<
    ReturnType<typeof resolveProvinciaAgencia>
  > | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [apply, setApply] = useState(false);
  const [progress, setProgress] = useState<{
    processed: number;
    total: number;
    asignadas: number;
    ambiguas: number;
    sinResultado: number;
  } | null>(null);
  const [pendientes, setPendientes] = useState<
    Array<{ id: string; nombre: string; region: string | null; motivo: string }>
  >([]);

  const fetcher = useAgenciaFetcher();
  const resolveOne = useServerFn(resolveProvinciaAgencia);
  const assignOne = useServerFn(assignProvinciaAgencia);
  const runBatch = useServerFn(resolveProvinciaBatch);

  async function handleResolve() {
    if (!agenciaId) return;
    setLoading(true);
    try {
      setSingle(await resolveOne({ data: { agencia_id: agenciaId } }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setLoading(false);
    }
  }

  async function handleAssign() {
    if (!single?.result.provincia_id) return;
    try {
      await assignOne({
        data: {
          agencia_id: single.agencia.id,
          provincia_id: single.result.provincia_id,
        },
      });
      toast.success(`Provincia asignada: ${single.result.provincia_nombre}`);
      setSingle({
        ...single,
        agencia: { ...single.agencia, provincia_id: single.result.provincia_id },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  }

  async function handleBatch() {
    setBatchRunning(true);
    setPendientes([]);
    const acc = { processed: 0, total: 0, asignadas: 0, ambiguas: 0, sinResultado: 0 };
    const noResueltas: Array<{
      id: string;
      nombre: string;
      region: string | null;
      motivo: string;
    }> = [];
    let offset = 0;
    try {
      for (let i = 0; i < 200; i++) {
        const res = await runBatch({ data: { offset, limit: 200, apply } });
        acc.processed += res.processed;
        acc.total = res.total;
        acc.asignadas += res.asignadas;
        acc.ambiguas += res.ambiguas;
        acc.sinResultado += res.sinResultado;
        for (const p of res.pendientes) {
          if (noResueltas.length < 300) noResueltas.push(p);
        }
        setProgress({ ...acc });
        offset = res.nextOffset;
        if (res.processed === 0 || offset >= res.total) break;
      }
      setPendientes(noResueltas);
      toast.success(
        apply
          ? `Proceso completado: ${acc.asignadas} agencias actualizadas.`
          : `Simulación completada: ${acc.asignadas} resolubles.`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBatchRunning(false);
    }
  }

  return (
    <div className="mt-8 rounded-lg border p-4">
      <div className="mb-3">
        <h3 className="font-semibold">Provincia por región</h3>
        <p className="text-xs text-muted-foreground">
          Deduce la provincia de una agencia a partir del texto de su región
          (puede ser provincia o ciudad). El proceso masivo sólo recorre
          agencias <b>sin provincia asignada</b> y <b>con región rellenada</b>;
          el resto se omite.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1">
          <Label className="mb-1 block text-xs">Agencia</Label>
          <AsyncEntityCombobox
            value={agenciaId}
            onChange={setAgenciaId}
            fetcher={fetcher}
            cacheKey="tools-agencias"
            placeholder="Selecciona una agencia"
            allowClear
          />
        </div>
        <Button onClick={handleResolve} disabled={!agenciaId || loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Probar
        </Button>
      </div>

      {single && (
        <div className="mt-4 rounded-md border bg-muted/30 p-3 text-sm">
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-medium">{single.agencia.nombre}</span>
            <span className="text-muted-foreground">
              Región: {single.agencia.region || "—"}
            </span>
            <span className="text-muted-foreground">
              Normalizado: <code>{single.result.normalized || "—"}</code>
            </span>
          </div>
          <div className="mb-2">
            {single.result.provincia_id ? (
              <span className="text-emerald-600">
                Provincia propuesta: <b>{single.result.provincia_nombre}</b> ·{" "}
                {single.result.reason}
              </span>
            ) : (
              <span className="text-destructive">
                Sin provincia: {single.result.reason}
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-1">#</th>
                  <th className="p-1">Intento</th>
                  <th className="p-1">Búsqueda</th>
                  <th className="p-1 text-right">Coincidencias</th>
                  <th className="p-1">Provincias candidatas</th>
                </tr>
              </thead>
              <tbody>
                {single.result.steps.map((s) => (
                  <tr key={s.step} className="border-b last:border-0">
                    <td className="p-1 tabular-nums">{s.step}</td>
                    <td className="p-1">{s.name}</td>
                    <td className="p-1"><code>{s.query}</code></td>
                    <td className="p-1 text-right tabular-nums">{s.found}</td>
                    <td className="p-1">{s.sample.join(" / ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {single.result.provincia_id && (
            <div className="mt-3">
              <Button size="sm" onClick={handleAssign}>
                Asignar provincia
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 border-t pt-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={handleBatch} disabled={batchRunning}>
            {batchRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Ejecutar en todas las agencias
          </Button>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={apply} onCheckedChange={setApply} disabled={batchRunning} />
            Guardar cambios (si está desactivado, sólo simula)
          </label>
        </div>

        {progress && (
          <div className="mt-3 text-sm">
            Procesadas {progress.processed} de {progress.total} · asignadas{" "}
            <b className="text-emerald-600">{progress.asignadas}</b> · ambiguas{" "}
            {progress.ambiguas} · sin resultado {progress.sinResultado}
          </div>
        )}

        {pendientes.length > 0 && (
          <div className="mt-3 max-h-80 overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-2">Agencia</th>
                  <th className="p-2">Región</th>
                  <th className="p-2">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="p-2">{p.nombre}</td>
                    <td className="p-2">{p.region || "—"}</td>
                    <td className="p-2 text-muted-foreground">{p.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EnriquecerAgencia() {
  const [agenciaId, setAgenciaId] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Awaited<
    ReturnType<typeof enrichAgenciaPreview>
  > | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [apply, setApply] = useState(false);
  const [soloIncompletas, setSoloIncompletas] = useState(true);
  const [concurrency, setConcurrency] = useState(5);
  const [actualizadasAntesDe, setActualizadasAntesDe] = useState("");

  const [progress, setProgress] = useState<{
    processed: number;
    total: number;
    actualizadas: number;
    sinDatos: number;
    errores: number;
  } | null>(null);
  const [noResueltas, setNoResueltas] = useState<
    Array<{ id: string; nombre: string; motivo: string }>
  >([]);
  const stopRef = useRef(false);

  const fetcher = useAgenciaFetcher();
  const runPreview = useServerFn(enrichAgenciaPreview);
  const runBatch = useServerFn(enrichAgenciasBatch);

  async function handlePreview() {
    if (!agenciaId) return;
    setLoading(true);
    try {
      setPreview(await runPreview({ data: { agencia_id: agenciaId, overwrite } }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setLoading(false);
    }
  }


  async function handleBatch() {
    setBatchRunning(true);
    stopRef.current = false;
    setNoResueltas([]);
    const acc = { processed: 0, total: 0, actualizadas: 0, sinDatos: 0, errores: 0 };
    const fails: Array<{ id: string; nombre: string; motivo: string }> = [];
    let offset = 0;
    try {
      for (let i = 0; i < 2000; i++) {
        if (stopRef.current) break;
        const res = await runBatch({
          data: {
            offset,
            limit: Math.max(10, concurrency * 2),
            apply,
            overwrite,
            soloIncompletas,
            concurrency,
            ...(actualizadasAntesDe ? { actualizadasAntesDe } : {}),
          },
        });

        acc.processed += res.processed;
        acc.total = res.total;
        acc.actualizadas += res.actualizadas;
        acc.sinDatos += res.sinDatos;
        acc.errores += res.errores;
        for (const p of res.noResueltas) if (fails.length < 300) fails.push(p);
        setProgress({ ...acc });
        setNoResueltas([...fails]);
        offset = res.nextOffset;
        if (res.processed === 0 || offset >= res.total) break;
      }
      toast.success(
        apply
          ? `Proceso terminado: ${acc.actualizadas} agencias actualizadas.`
          : `Simulación terminada: ${acc.actualizadas} con datos nuevos.`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBatchRunning(false);
    }
  }

  return (
    <div className="mt-8 rounded-lg border p-4">
      <div className="mb-3">
        <h3 className="font-semibold">Enriquecer agencia (teléfono, email, web…)</h3>
        <p className="text-xs text-muted-foreground">
          Usa exactamente el mismo enriquecimiento con IA que el importador
          (playground · Contacto/Agencia): busca online el negocio y devuelve web,
          teléfono, correo y dirección.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1">
          <Label className="mb-1 block text-xs">Agencia</Label>
          <AsyncEntityCombobox
            value={agenciaId}
            onChange={setAgenciaId}
            fetcher={fetcher}
            cacheKey="tools-agencias-enrich"
            placeholder="Selecciona una agencia"
            allowClear
          />
        </div>
        <Button onClick={handlePreview} disabled={!agenciaId || loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Probar
        </Button>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={overwrite} onCheckedChange={setOverwrite} />
          Sobrescribir datos existentes
        </label>
      </div>

      {preview && (
        <div className="mt-4">
          <AgenciaEnrichPreviewView preview={preview} onApplied={() => setPreview(null)} />
        </div>
      )}


      <div className="mt-6 border-t pt-4">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Alcance:</span>
          <Button
            variant={soloIncompletas ? "default" : "outline"}
            size="sm"
            disabled={batchRunning}
            onClick={() => setSoloIncompletas(true)}
          >
            Sólo sin email o teléfono
          </Button>
          <Button
            variant={soloIncompletas ? "outline" : "default"}
            size="sm"
            disabled={batchRunning}
            onClick={() => setSoloIncompletas(false)}
          >
            Todas las agencias
          </Button>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">En paralelo:</span>
          {[1, 5, 10].map((n) => (
            <Button
              key={n}
              variant={concurrency === n ? "default" : "outline"}
              size="sm"
              disabled={batchRunning}
              onClick={() => setConcurrency(n)}
            >
              {n}
            </Button>
          ))}
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <Label className="text-muted-foreground">Sólo actualizadas antes de:</Label>
          <DatePicker
            className="w-[170px]"
            value={actualizadasAntesDe}
            disabled={batchRunning}
            onChange={setActualizadasAntesDe}
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={batchRunning}
            onClick={() => setActualizadasAntesDe(new Date().toISOString().slice(0, 10))}
          >
            Hoy
          </Button>
          {actualizadasAntesDe && (
            <Button
              variant="ghost"
              size="sm"
              disabled={batchRunning}
              onClick={() => setActualizadasAntesDe("")}
            >
              Quitar
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={handleBatch} disabled={batchRunning}>
            {batchRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {soloIncompletas
              ? "Ejecutar en agencias incompletas"
              : "Ejecutar en todas las agencias"}
          </Button>
          {batchRunning && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                stopRef.current = true;
              }}
            >
              Detener
            </Button>
          )}
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={apply} onCheckedChange={setApply} disabled={batchRunning} />
            Guardar cambios (si está desactivado, sólo simula)
          </label>
        </div>

        <p className="mt-2 text-xs text-muted-foreground">
          Ojo: se hace una llamada de IA por agencia, así que en todas las agencias
          el proceso es lento y tiene coste. Puedes detenerlo cuando quieras.
        </p>

        {progress && (
          <div className="mt-3 text-sm">
            Procesadas {progress.processed} de {progress.total} · con datos nuevos{" "}
            <b className="text-emerald-600">{progress.actualizadas}</b> · sin cambios{" "}
            {progress.sinDatos} · errores{" "}
            <b className="text-destructive">{progress.errores}</b>
          </div>
        )}

        {noResueltas.length > 0 && (
          <div className="mt-3 max-h-80 overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-2">Agencia</th>
                  <th className="p-2">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {noResueltas.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="p-2">{p.nombre}</td>
                    <td className="p-2 text-muted-foreground">{p.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



function ScrapingbeeUsage() {
  const [rows, setRows] = useState<Array<{ day: string; endpoint: string; calls: number; ok: number; errors: number; credits: number }> | null>(null);
  const [loading, setLoading] = useState(false);
  const run = useServerFn(getScrapingbeeStats);

  async function load() {
    setLoading(true);
    try {
      const res = await run();
      setRows(res.rows);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setLoading(false);
    }
  }

  const byDay = new Map<string, { calls: number; credits: number; errors: number }>();
  for (const r of rows ?? []) {
    const cur = byDay.get(r.day) ?? { calls: 0, credits: 0, errors: 0 };
    cur.calls += r.calls;
    cur.credits += r.credits;
    cur.errors += r.errors;
    byDay.set(r.day, cur);
  }

  return (
    <div className="mt-8 rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Consumo ScrapingBee (últimos 7 días)</h3>
          <p className="text-xs text-muted-foreground">Cada llamada se registra en <code>scrapingbee_calls</code>.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {rows ? "Recargar" : "Cargar"}
        </Button>
      </div>
      {rows && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="p-2">Día</th>
                <th className="p-2">Endpoint</th>
                <th className="p-2 text-right">Llamadas</th>
                <th className="p-2 text-right">OK</th>
                <th className="p-2 text-right">Errores</th>
                <th className="p-2 text-right">Créditos (est.)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="p-2 tabular-nums">{r.day}</td>
                  <td className="p-2">{r.endpoint}</td>
                  <td className="p-2 text-right tabular-nums">{r.calls}</td>
                  <td className="p-2 text-right tabular-nums text-emerald-600">{r.ok}</td>
                  <td className="p-2 text-right tabular-nums text-destructive">{r.errors}</td>
                  <td className="p-2 text-right tabular-nums font-medium">{r.credits.toLocaleString()}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Sin datos aún.</td></tr>
              )}
            </tbody>
            {byDay.size > 0 && (
              <tfoot>
                {Array.from(byDay.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([day, t]) => (
                  <tr key={`t-${day}`} className="bg-muted/30 font-medium">
                    <td className="p-2">{day} · total</td>
                    <td className="p-2 text-muted-foreground">—</td>
                    <td className="p-2 text-right tabular-nums">{t.calls}</td>
                    <td className="p-2 text-right tabular-nums" />
                    <td className="p-2 text-right tabular-nums text-destructive">{t.errors}</td>
                    <td className="p-2 text-right tabular-nums">{t.credits.toLocaleString()}</td>
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function toCsv(rows: Array<{ nombre: string; telefono: string | null; email: string | null }>) {
  const escape = (v: string | null | undefined) => {
    const s = (v ?? "").replace(/"/g, '""');
    return /[",\n;]/.test(s) ? `"${s}"` : s;
  };
  const header = "nombre,telefono,email";
  const body = rows.map((r) => `${escape(r.nombre)},${escape(r.telefono)},${escape(r.email)}`).join("\n");
  return `${header}\n${body}\n`;
}

export function AgenciasEmailDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const run = useServerFn(findAgenciasByEmailSubject);

  async function handleRun() {
    if (!subject.trim()) return;
    setLoading(true);
    try {
      const res = await run({ data: { subject: subject.trim() } });
      if (!res.agencias.length) {
        toast.info(`Sin agencias. Emails encontrados: ${res.matched_emails}.`);
        return;
      }
      const csv = toCsv(res.agencias);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agencias-email-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${res.agencias.length} agencias exportadas.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al generar el CSV");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agencias con email enviados</DialogTitle>
          <DialogDescription>
            Encuentra las agencias a partir de los emails enviados. Introduce un texto que contenga el asunto (subject) de los emails a buscar. Se buscarán los correos coincidentes, se extraerán los destinatarios y se cruzarán con las agencias por email. El resultado es un CSV con nombre, teléfono y correo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="subject">Asunto contiene</Label>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ej. Hemos publicado GRATIS"
            disabled={loading}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleRun} disabled={loading || !subject.trim()}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Generar CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
